/**
 * Simulation Store — State Management
 *
 * Centralized state model for all simulation operations.
 * Framework-agnostic (vanilla TS with subscriber pattern).
 * Can be wrapped with React context/hooks.
 */

import type {
  SimulationStatus,
  SimulationResult,
  SimulationError,
  SimulationType,
  RunSimulationRequest,
  ReplaySimulationRequest,
  StressSimulationRequest,
} from '../types/simulation';

import {
  runSimulation,
  runReplay,
  runStressTest,
  isSimulationError,
  enforceConcurrencyGuard,
  setSimulationActive,
  cancelActiveSimulation,
  validateSimulationResult,
  classifyError,
} from '../services/simulationAdapter';

import type { SimulationErrorType } from '../types/simulation';

// ═══════════════════════════════════════════════════════════════════════
//  STATE INTERFACE
// ═══════════════════════════════════════════════════════════════════════

export interface SimulationState {
  status: SimulationStatus;
  simulationType: SimulationType | null;
  simulationId: string | null;
  progress: number;
  results: SimulationResult | null;
  error: SimulationError | null;
  history: SimulationHistoryEntry[];
  /** Phase 2: Retry visibility */
  retrying: boolean;
  retryCount: number;
  /** Phase 2: Partial results (live updates while running) */
  partialResults: SimulationResult | null;
  /** Phase 2: Enhanced error classification */
  errorType: SimulationErrorType | null;
}

export interface SimulationHistoryEntry {
  simulationId: string;
  type: SimulationType;
  status: SimulationStatus;
  startedAt: string;
  completedAt: string | null;
  durationMs: number | null;
  summary: string;
}

// ═══════════════════════════════════════════════════════════════════════
//  INITIAL STATE
// ═══════════════════════════════════════════════════════════════════════

const INITIAL_STATE: SimulationState = {
  status: 'idle',
  simulationType: null,
  simulationId: null,
  progress: 0,
  results: null,
  error: null,
  history: [],
  retrying: false,
  retryCount: 0,
  partialResults: null,
  errorType: null,
};

// ═══════════════════════════════════════════════════════════════════════
//  STORE
// ═══════════════════════════════════════════════════════════════════════

type Listener = (state: SimulationState) => void;

class SimulationStoreClass {
  private state: SimulationState = { ...INITIAL_STATE };
  private listeners: Set<Listener> = new Set();

  // ── Getters ──────────────────────────────────────────────────

  getState(): Readonly<SimulationState> {
    return this.state;
  }

  getStatus(): SimulationStatus {
    return this.state.status;
  }

  getResults(): SimulationResult | null {
    return this.state.results;
  }

  getError(): SimulationError | null {
    return this.state.error;
  }

  getHistory(): SimulationHistoryEntry[] {
    return this.state.history;
  }

  isRunning(): boolean {
    return this.state.status === 'running' || this.state.status === 'loading';
  }

  // ── Subscription ─────────────────────────────────────────────

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    const snapshot = { ...this.state };
    this.listeners.forEach((fn) => fn(snapshot));
  }

  private setState(partial: Partial<SimulationState>): void {
    this.state = { ...this.state, ...partial };
    this.notify();
  }

  // ── Actions ──────────────────────────────────────────────────

  startSimulation(type: SimulationType, simulationId?: string): void {
    SimulationLogger.info(`Starting ${type} simulation`, { simulationId });
    this.setState({
      status: 'loading',
      simulationType: type,
      simulationId: simulationId || null,
      progress: 0,
      results: null,
      error: null,
    });
  }

  updateProgress(progress: number, simulationId?: string): void {
    const clamped = Math.max(0, Math.min(100, progress));
    this.setState({
      status: 'running',
      progress: clamped,
      simulationId: simulationId || this.state.simulationId,
    });
  }

  completeSimulation(result: SimulationResult): void {
    const entry: SimulationHistoryEntry = {
      simulationId: result.simulationId,
      type: this.state.simulationType || 'live',
      status: 'success',
      startedAt: new Date().toISOString(),
      completedAt: new Date().toISOString(),
      durationMs: null,
      summary: `${result?.summary?.strategy ?? 'simulation'} — ${(result?.metrics?.totalReturnPct ?? 0).toFixed(2)}% return`,
    };

    SimulationLogger.info('Simulation completed', {
      id: result.simulationId,
      returnPct: result.metrics.totalReturnPct,
      trades: result.metrics.totalTrades,
    });

    this.setState({
      status: 'success',
      progress: 100,
      results: result,
      error: null,
      simulationId: result.simulationId,
      history: [entry, ...this.state.history].slice(0, 50),
    });
  }

  failSimulation(error: SimulationError): void {
    const entry: SimulationHistoryEntry = {
      simulationId: this.state.simulationId || 'unknown',
      type: this.state.simulationType || 'live',
      status: 'error',
      startedAt: new Date().toISOString(),
      completedAt: new Date().toISOString(),
      durationMs: null,
      summary: `Failed: ${error.userMessage}`,
    };

    SimulationLogger.error('Simulation failed', error);

    this.setState({
      status: 'error',
      progress: 0,
      results: null,
      error,
      errorType: classifyError(error),
      retrying: false,
      history: [entry, ...this.state.history].slice(0, 50),
    });
  }

  reset(): void {
    SimulationLogger.info('Store reset');
    setSimulationActive(false);
    this.setState({
      status: 'idle',
      simulationType: null,
      simulationId: null,
      progress: 0,
      results: null,
      error: null,
      retrying: false,
      retryCount: 0,
      partialResults: null,
      errorType: null,
    });
  }

  clearHistory(): void {
    this.setState({ history: [] });
  }

  // ── Phase 2: Retry Visibility ───────────────────────────────

  startRetry(attempt: number): void {
    SimulationLogger.info(`Retry attempt ${attempt}`);
    this.setState({ retrying: true, retryCount: attempt });
  }

  endRetry(): void {
    this.setState({ retrying: false });
  }

  // ── Phase 2: Partial Results ────────────────────────────────

  updatePartialResults(partial: SimulationResult): void {
    SimulationLogger.info('Partial results received', { id: partial.simulationId });
    this.setState({
      status: 'partial',
      partialResults: partial,
      simulationId: partial.simulationId,
    });
  }

  // ── Phase 2: Cancellation ──────────────────────────────────

  cancelSimulation(): void {
    SimulationLogger.info('Simulation cancelled by user');
    cancelActiveSimulation();
    setSimulationActive(false);
    this.setState({
      status: 'idle',
      progress: 0,
      retrying: false,
      retryCount: 0,
      partialResults: null,
    });
  }

  // ── High-level orchestrators ─────────────────────────────────

  async executeRunSimulation(request: RunSimulationRequest): Promise<SimulationResult> {
    enforceConcurrencyGuard();
    setSimulationActive(true);

    const startTime = performance.now();
    this.startSimulation('live');

    try {
      // Simulate progress updates
      this.updateProgress(10);
      const progressInterval = setInterval(() => {
        const current = this.state.progress;
        if (current < 90) {
          this.updateProgress(current + Math.random() * 15);
        }
      }, 500);

      const result = await runSimulation(request);

      clearInterval(progressInterval);
      validateSimulationResult(result);
      this.completeSimulation(result);

      // Update duration in history
      const durationMs = Math.round(performance.now() - startTime);
      if (this.state.history.length > 0) {
        this.state.history[0].durationMs = durationMs;
      }

      setSimulationActive(false);
      return result;
    } catch (err) {
      setSimulationActive(false);
      const error = isSimulationError(err)
        ? err
        : { code: 'UNKNOWN' as const, message: String(err), userMessage: 'An unexpected error occurred.', retryable: false };
      this.failSimulation(error);
      throw error;
    }
  }

  async executeReplaySimulation(request: ReplaySimulationRequest): Promise<SimulationResult> {
    enforceConcurrencyGuard();
    setSimulationActive(true);

    const startTime = performance.now();
    this.startSimulation('replay');

    try {
      this.updateProgress(10);
      const progressInterval = setInterval(() => {
        const current = this.state.progress;
        if (current < 90) {
          this.updateProgress(current + Math.random() * 20);
        }
      }, 300);

      const result = await runReplay(request);

      clearInterval(progressInterval);
      validateSimulationResult(result);
      this.completeSimulation(result);

      const durationMs = Math.round(performance.now() - startTime);
      if (this.state.history.length > 0) {
        this.state.history[0].durationMs = durationMs;
      }

      setSimulationActive(false);
      return result;
    } catch (err) {
      setSimulationActive(false);
      const error = isSimulationError(err)
        ? err
        : { code: 'UNKNOWN' as const, message: String(err), userMessage: 'An unexpected error occurred.', retryable: false };
      this.failSimulation(error);
      throw error;
    }
  }

  async executeStressTest(request: StressSimulationRequest): Promise<SimulationResult> {
    enforceConcurrencyGuard();
    setSimulationActive(true);

    const startTime = performance.now();
    this.startSimulation('stress');

    try {
      this.updateProgress(5);
      const progressInterval = setInterval(() => {
        const current = this.state.progress;
        if (current < 85) {
          this.updateProgress(current + Math.random() * 10);
        }
      }, 600);

      const result = await runStressTest(request);

      clearInterval(progressInterval);
      validateSimulationResult(result);
      this.completeSimulation(result);

      const durationMs = Math.round(performance.now() - startTime);
      if (this.state.history.length > 0) {
        this.state.history[0].durationMs = durationMs;
      }

      setSimulationActive(false);
      return result;
    } catch (err) {
      setSimulationActive(false);
      const error = isSimulationError(err)
        ? err
        : { code: 'UNKNOWN' as const, message: String(err), userMessage: 'An unexpected error occurred.', retryable: false };
      this.failSimulation(error);
      throw error;
    }
  }
}

// ═══════════════════════════════════════════════════════════════════════
//  LOGGER — Console + Telemetry Hook (Phase 7)
// ═══════════════════════════════════════════════════════════════════════

type TelemetryHook = (event: string, data: Record<string, unknown>) => void;

let telemetryHook: TelemetryHook | null = null;

export function setTelemetryHook(hook: TelemetryHook): void {
  telemetryHook = hook;
}

export const SimulationLogger = {
  requestStart(endpoint: string, requestId: string): void {
    console.log(`[Simulation] ▶ ${endpoint}  reqId=${requestId}`);
    telemetryHook?.('simulation.request.start', { endpoint, requestId, timestamp: Date.now() });
  },

  requestEnd(endpoint: string, requestId: string, durationMs: number, success: boolean, errorCode?: string): void {
    const icon = success ? '✓' : '✗';
    console.log(`[Simulation] ${icon} ${endpoint}  ${durationMs}ms  reqId=${requestId}${errorCode ? `  err=${errorCode}` : ''}`);
    telemetryHook?.('simulation.request.end', { endpoint, requestId, durationMs, success, errorCode, timestamp: Date.now() });
  },

  retrying(endpoint: string, attempt: number, backoffMs: number): void {
    console.warn(`[Simulation] ↻ Retrying ${endpoint}  attempt=${attempt}  backoff=${backoffMs}ms`);
    telemetryHook?.('simulation.request.retry', { endpoint, attempt, backoffMs, timestamp: Date.now() });
  },

  info(message: string, data?: Record<string, unknown>): void {
    console.log(`[Simulation] ℹ ${message}`, data || '');
    telemetryHook?.('simulation.info', { message, ...data, timestamp: Date.now() });
  },

  error(message: string, error: unknown): void {
    console.error(`[Simulation] ✗ ${message}`, error);
    telemetryHook?.('simulation.error', { message, error: String(error), timestamp: Date.now() });
  },
};

// ═══════════════════════════════════════════════════════════════════════
//  SINGLETON EXPORT
// ═══════════════════════════════════════════════════════════════════════

export const simulationStore = new SimulationStoreClass();
export default simulationStore;
